import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import {
  Users,
  Crown,
  UserPlus,
  Activity,
  ShoppingBag,
  DollarSign,
  Phone,
  TrendingUp,
  BarChart3,
  CreditCard,
  Star
} from 'lucide-react';

interface CustomerAnalyticsProps {
  dateRange: { from: string; to: string };
}

interface CustomerAnalyticsData {
  summary: {
    total_customers: number;
    vip_customers: number;
    active_in_period: number;
    new_in_period: number;
    avg_orders_per_customer: number;
    avg_spent_per_customer: number;
    avg_order_value: number;
  };
  top_by_revenue: TopCustomer[];
  top_by_orders: TopCustomer[];
  new_customers_trend: { date: string; count: number }[];
  retention: { total_unique: number; returning: number; new: number };
  order_frequency: { bucket: string; count: number }[];
  payment_methods: { method: string; count: number }[];
}

interface TopCustomer {
  id: number;
  name: string;
  phone: string;
  total_orders: number;
  total_spent: number;
  average_order_value: number;
  last_order_date?: string;
  is_vip: boolean;
  loyalty_points: number;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' }).format(amount);

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const CustomerAnalytics: React.FC<CustomerAnalyticsProps> = ({ dateRange }) => {
  const { t } = useTranslation(['analytics', 'common']);
  const { store } = useAuth();
  const [data, setData] = useState<CustomerAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [topView, setTopView] = useState<'revenue' | 'orders'>('revenue');

  const paymentLabels: Record<string, string> = {
    cash: t('analytics:customer.paymentCash'),
    card: t('analytics:customer.paymentCard'),
    online: 'Online',
    'Άγνωστο': t('analytics:customer.paymentUnknown'),
  };

  useEffect(() => {
    if (store?.id) fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id, dateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/analytics/stores/${store?.id}/customers`, {
        params: { from: dateRange.from, to: dateRange.to }
      });
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch customer analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-t3">{t('analytics:customer.loadingAnalytics')}</div>;
  }

  if (!data) {
    return <div className="text-center py-16 text-t4">{t('analytics:customer.noCustomerData')}</div>;
  }

  const { summary, retention, order_frequency, payment_methods } = data;
  const topList = topView === 'revenue' ? data.top_by_revenue : data.top_by_orders;

  // Retention percentage
  const retentionPct = retention.total_unique > 0
    ? ((retention.returning / retention.total_unique) * 100).toFixed(1)
    : '0.0';

  // Max for frequency bar chart
  const maxFreq = Math.max(...order_frequency.map(f => f.count), 1);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          icon={Users}
          label={t('analytics:customer.totalCustomers')}
          value={summary.total_customers}
          color="text-blue-600 dark:text-blue-400"
          bgColor="bg-blue-500/10"
        />
        <SummaryCard
          icon={Crown}
          label={t('analytics:customer.vipCustomers')}
          value={summary.vip_customers}
          color="text-amber-700 dark:text-amber-400"
          bgColor="bg-amber-500/10"
        />
        <SummaryCard
          icon={UserPlus}
          label={t('analytics:customer.newInPeriod')}
          value={summary.new_in_period}
          color="text-emerald-600 dark:text-emerald-400"
          bgColor="bg-emerald-500/10"
        />
        <SummaryCard
          icon={Activity}
          label={t('analytics:customer.activeInPeriod')}
          value={summary.active_in_period}
          color="text-purple-600 dark:text-purple-400"
          bgColor="bg-purple-500/10"
        />
      </div>

      {/* Averages Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line p-5">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingBag className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm text-t3">{t('analytics:customer.avgOrdersPerCustomer')}</span>
          </div>
          <div className="text-2xl font-bold text-t1">{summary.avg_orders_per_customer.toFixed(1)}</div>
        </div>
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line p-5">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            <span className="text-sm text-t3">{t('analytics:customer.avgRevenuePerCustomer')}</span>
          </div>
          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(summary.avg_spent_per_customer)}</div>
        </div>
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line p-5">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            <span className="text-sm text-t3">{t('analytics:customer.avgOrderValueLabel')}</span>
          </div>
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{formatCurrency(summary.avg_order_value)}</div>
        </div>
      </div>

      {/* Retention & Frequency Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Retention Donut */}
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1">{t('analytics:customer.newVsReturning')}</h3>
            <p className="text-xs text-t4 mt-0.5">{t('analytics:customer.uniqueCustomersInPeriod')}</p>
          </div>
          <div className="p-5">
            {retention.total_unique === 0 ? (
              <div className="text-center py-8 text-t4">{t('analytics:customer.noData')}</div>
            ) : (
              <div className="flex items-center gap-6">
                {/* Visual ring */}
                <div className="relative w-28 h-28 flex-shrink-0">
                  <svg viewBox="0 0 36 36" className="w-28 h-28 -rotate-90">
                    <circle cx="18" cy="18" r="15.5" fill="none" stroke="#334155" strokeWidth="3" />
                    <circle
                      cx="18" cy="18" r="15.5" fill="none"
                      stroke="#10b981"
                      strokeWidth="3"
                      strokeDasharray={`${(retention.returning / retention.total_unique) * 97.4} 97.4`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-lg font-bold text-t1">{retentionPct}%</span>
                    <span className="text-[10px] text-t4">retention</span>
                  </div>
                </div>
                <div className="space-y-3 flex-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" />
                      <span className="text-sm text-t2">{t('analytics:customer.returning')}</span>
                    </div>
                    <span className="text-sm font-semibold text-t1">{retention.returning}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                      <span className="text-sm text-t2">{t('analytics:customer.new')}</span>
                    </div>
                    <span className="text-sm font-semibold text-t1">{retention.new}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-line">
                    <span className="text-sm text-t3">{t('analytics:customer.totalUnique')}</span>
                    <span className="text-sm font-semibold text-t1">{retention.total_unique}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Order Frequency Distribution */}
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1">{t('analytics:customer.frequencyDistribution')}</h3>
            <p className="text-xs text-t4 mt-0.5">{t('analytics:customer.customersPerOrderCount')}</p>
          </div>
          <div className="p-5 space-y-3">
            {order_frequency.length === 0 ? (
              <div className="text-center py-8 text-t4">{t('analytics:customer.noData')}</div>
            ) : (
              order_frequency.map((f) => (
                <div key={f.bucket}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-t2">{f.bucket}</span>
                    <span className="text-sm font-medium text-t1">{f.count}</span>
                  </div>
                  <div className="w-full h-2 bg-surface-2 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all"
                      style={{ width: `${(f.count / maxFreq) * 100}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Payment Methods & Top Customers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Payment Methods */}
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> {t('analytics:customer.paymentMethods')}
            </h3>
          </div>
          <div className="p-5 space-y-3">
            {payment_methods.length === 0 ? (
              <div className="text-center py-4 text-t4">-</div>
            ) : (
              payment_methods.map((pm) => {
                const totalPM = payment_methods.reduce((s, p) => s + p.count, 0);
                const pct = totalPM > 0 ? ((pm.count / totalPM) * 100).toFixed(1) : '0';
                return (
                  <div key={pm.method} className="flex items-center justify-between">
                    <span className="text-sm text-t2">{paymentLabels[pm.method] || pm.method}</span>
                    <div className="text-right">
                      <span className="text-sm font-semibold text-t1">{pm.count}</span>
                      <span className="text-xs text-t4 ml-1.5">({pct}%)</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Top Customers */}
        <div className="lg:col-span-2 bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-700 dark:text-amber-400" /> {t('analytics:customer.top10Customers')}
            </h3>
            <div className="flex gap-1.5">
              <button
                onClick={() => setTopView('revenue')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all active:scale-95 ${
                  topView === 'revenue'
                    ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                    : 'bg-surface-2 text-t3 hover:text-t1'
                }`}
              >
                {t('analytics:customer.byRevenue')}
              </button>
              <button
                onClick={() => setTopView('orders')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all active:scale-95 ${
                  topView === 'orders'
                    ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400'
                    : 'bg-surface-2 text-t3 hover:text-t1'
                }`}
              >
                {t('analytics:customer.byOrders')}
              </button>
            </div>
          </div>
          <div className="p-5">
            {topList.length === 0 ? (
              <div className="text-center py-8 text-t4">{t('analytics:customer.noCustomers')}</div>
            ) : (
              <>
                {/* Mobile: Cards */}
                <div className="md:hidden space-y-3">
                  {topList.map((customer, idx) => (
                    <div key={customer.id} className="bg-surface-2 dark:bg-surface-2/40 rounded-xl p-3 border border-line/60 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-400 flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-t1 truncate">{customer.name || t('analytics:customer.unknown')}</span>
                          {customer.is_vip && <span className="px-1 py-0.5 bg-amber-500/20 text-amber-700 dark:text-amber-400 rounded text-[9px] font-bold">VIP</span>}
                        </div>
                        <div className="text-xs text-t4 flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {customer.phone}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">{formatCurrency(customer.total_spent)}</div>
                        <div className="text-[11px] text-t4">{customer.total_orders} {t('analytics:customer.ordersShort')}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop: Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-line">
                        <th className="text-left py-2.5 px-3 text-xs font-semibold text-t4 uppercase">#</th>
                        <th className="text-left py-2.5 px-3 text-xs font-semibold text-t4 uppercase">{t('analytics:customer.customerColumn')}</th>
                        <th className="text-right py-2.5 px-3 text-xs font-semibold text-t4 uppercase">{t('analytics:orders')}</th>
                        <th className="text-right py-2.5 px-3 text-xs font-semibold text-t4 uppercase">{t('analytics:totalColumn')}</th>
                        <th className="text-right py-2.5 px-3 text-xs font-semibold text-t4 uppercase">{t('analytics:customer.avgShort')}</th>
                        <th className="text-right py-2.5 px-3 text-xs font-semibold text-t4 uppercase">{t('analytics:customer.lastOrder')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topList.map((customer, idx) => (
                        <tr key={customer.id} className="border-b border-line/50 hover:bg-surface-3 dark:hover:bg-surface-2/30">
                          <td className="py-2.5 px-3">
                            <div className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-400 flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </div>
                          </td>
                          <td className="py-2.5 px-3">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-medium text-t1">{customer.name || t('analytics:customer.unknown')}</span>
                              {customer.is_vip && <span className="px-1 py-0.5 bg-amber-500/20 text-amber-700 dark:text-amber-400 rounded text-[9px] font-bold">VIP</span>}
                            </div>
                            <div className="text-xs text-t4">{customer.phone}</div>
                          </td>
                          <td className="py-2.5 px-3 text-right text-sm text-t1 font-medium">{customer.total_orders}</td>
                          <td className="py-2.5 px-3 text-right text-sm text-emerald-600 dark:text-emerald-400 font-medium">{formatCurrency(customer.total_spent)}</td>
                          <td className="py-2.5 px-3 text-right text-sm text-t3">{formatCurrency(customer.average_order_value)}</td>
                          <td className="py-2.5 px-3 text-right text-xs text-t4">{formatDate(customer.last_order_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* New Customers Trend */}
      {data.new_customers_trend.length > 0 && (
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-600 dark:text-blue-400" /> {t('analytics:customer.newCustomersPerDay')}
            </h3>
          </div>
          <div className="p-5">
            <div className="flex items-end gap-1 h-32 overflow-x-auto">
              {data.new_customers_trend.map((day) => {
                const maxCount = Math.max(...data.new_customers_trend.map(d => d.count), 1);
                const height = (day.count / maxCount) * 100;
                return (
                  <div key={day.date} className="h-full flex flex-col items-center gap-1 min-w-[28px] flex-1" title={`${formatDate(day.date)}: ${day.count}`}>
                    <span className="text-[10px] text-t4">{day.count}</span>
                    <div className="flex-1 min-h-0 w-full flex items-end">
                      <div
                        className="w-full bg-blue-500/60 rounded-t-md transition-all hover:bg-blue-500"
                        style={{ height: `${Math.max(height, 4)}%` }}
                      />
                    </div>
                    <span className="text-[9px] text-t4 truncate w-full text-center">
                      {new Date(day.date).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Helper Components ──────────────────────────────────────────

const SummaryCard: React.FC<{
  icon: React.FC<any>;
  label: string;
  value: number;
  color: string;
  bgColor: string;
}> = ({ icon: Icon, label, value, color, bgColor }) => (
  <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line p-5">
    <div className={`w-10 h-10 rounded-xl ${bgColor} flex items-center justify-center mb-3`}>
      <Icon className={`h-5 w-5 ${color}`} />
    </div>
    <div className="text-2xl font-bold text-t1">{value.toLocaleString('el-GR')}</div>
    <div className="text-xs text-t3 mt-0.5">{label}</div>
  </div>
);

export default CustomerAnalytics;
